/**
 * ASO-AN-001 — PostHog identity lifecycle. The single place that decides WHO an event belongs to.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE DEFECT THIS MODULE EXISTS TO MAKE IMPOSSIBLE
 *
 * All 40 iOS `user_signed_up` events between 2026-08-02 and 2026-08-29 carried
 * `properties.$is_identified = false`, while later events in the same sessions carried `true`.
 *
 * The cause was purely one of ORDER, and it was structural rather than accidental:
 *
 *   * `user_signed_up` is captured in `screens/Login/FunnelScreen.js`, which lives in `AuthStack` —
 *     i.e. it renders while `userAtom` is still null.
 *   * `postHog.identify(user.uid, …)` lived ONLY in `AppStack.setUserInfoToAnalytics`, and that
 *     function is driven by the `organizations/{id}` snapshot. Reaching it requires: Firestore
 *     auth state → `Routes.setUserDetails` loads the user document → `userAtom` set → `AppStack`
 *     mounts → `getInitialData` attaches the org listener → first snapshot arrives.
 *
 * So identity was established several async hops AFTER the signup event had already left the
 * device. There was no race to lose: the signup event could never be identified.
 *
 * Two further holes sat on the same seam, and both are closed here rather than in a screen:
 *
 *   * `postHog.reset()` was called from exactly ONE place — the Profile screen's logout button —
 *     covering one of the five `logout()` call sites and coordinated with nothing. (An earlier
 *     version of this docblock said it was "called nowhere"; that was wrong, and the error mattered:
 *     the real defect was a SECOND uncoordinated owner of identity teardown, not an absent one.
 *     See utils/__tests__/analyticsIdentityOwnership.test.js.) The other four call sites — and a
 *     revoked token, and a deleted account — reset nothing, so PostHog, which persists
 *     `distinct_id` to disk, kept reporting the departed user until AppStack's late `identify()`,
 *     and aliased the two accounts into one person if the next user signed in.
 *   * Identity was only ever (re)established from org data. A returning user whose PostHog storage
 *     had been cleared (reinstall, or our own new `reset()` on logout) stayed anonymous for the
 *     whole pre-org window on every launch.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE IDENTITY MODEL (product-owner ruling, 2026-08-30)
 *
 *   PostHog person `distinct_id`        = Firebase UID          (a person)
 *   PostHog group `organization`        = organizationId        (a tenant)
 *   RevenueCat `app_user_id`            = organizationId        (see utils/revenueCatIdentity.js)
 *
 * An Atlas entitlement belongs to the ORGANIZATION, not to whoever happened to tap Buy. So
 * subscription lifecycle is joined to product behaviour at the organization-group level and NEVER
 * by asserting a person-level identity on a RevenueCat subscriber. That is why nothing in this
 * module writes a person id into RevenueCat.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY THE DECISION IS A PURE FUNCTION
 *
 * `resolveIdentityTransition` takes the identity PostHog currently holds and the identity Firebase
 * currently holds, and returns what to do. It has four cases and no I/O, so the rules that actually
 * matter — "merge anonymous pre-auth events into the new person", "never alias two real accounts" —
 * are asserted by tests instead of being emergent behaviour of a React effect.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * ANALYTICS IS NEVER LOAD-BEARING
 *
 * Same rule as utils/organizationAnalytics.js, learned from ATL-901 (one unguarded telemetry
 * dereference locked a paying customer out of their till on every platform for four hours). Every
 * function here tolerates a missing, partial or throwing PostHog client and returns a value instead
 * of propagating. A till must open even when analytics is broken.
 */

/**
 * The ONE canonical successful-signup event.
 *
 * The name is deliberately unchanged from what production has emitted since the funnel shipped, so
 * existing PostHog insights, funnels and dashboards keep working across this fix. What changes is
 * only the identity the event is attributed to — which is what makes the pre-fix events (40 of
 * them, all `$is_identified: false`) distinguishable from post-fix ones without renaming anything.
 * See docs/technical/analytics-identity.md for the migration note and the verification queries.
 */
export const SIGNUP_COMPLETED_EVENT = 'user_signed_up'

/**
 * How long identity work will wait for the PostHog SDK to finish loading its persisted state.
 *
 * There has to be a cap. `captureSignupCompleted` is awaited by `FunnelScreen.registerOrg` BEFORE it
 * navigates to Home, so an SDK whose `ready()` never settles — a wedged AsyncStorage read, a native
 * module that failed to initialise — would leave a user who has successfully created an account and
 * an organization staring at a spinner. This repo has been here before (ATL-889: an unbounded drain
 * wait that could hang the app), and the rule it produced applies exactly: telemetry may delay the
 * app briefly, never indefinitely.
 *
 * 2s is generous for a local storage read and short enough to be invisible against the several
 * seconds `createNewUserAndOrganization` already takes. On expiry the identity work proceeds on
 * whatever state the SDK reports, which fails toward "identify" rather than "skip".
 */
export const IDENTITY_READY_CAP_MS = 2000

/**
 * How long a sign-out will wait for the SUBSCRIPTION identity to be cleared before moving on.
 *
 * `createAuthIdentityHandler` runs auth callbacks strictly one after another, because two of them
 * racing can land a sign-out's `reset()` after the next person's `identify()`. That serialization
 * has a cost that has to be paid for explicitly: every await inside a queued callback is now a
 * HEAD-OF-LINE BLOCK on every later callback. Everything in `syncAnalyticsIdentity` is already
 * bounded (`IDENTITY_READY_CAP_MS`) or synchronous — `clearSubscriptionIdentity` is the one step
 * that crosses the React Native bridge into `Purchases.logOut()`, whose promise settles only when
 * the native module answers. An unresponsive one would otherwise wedge the queue for the life of
 * the process: the NEXT person to sign in would never be identified at all, which is a strictly
 * worse version of the defect this bridge exists to remove.
 *
 * Same rule as the readiness cap, and the same one ATL-889 produced: telemetry may delay work
 * briefly, never indefinitely. On expiry the clear is abandoned as unknown (reported as not
 * cleared) and identity work continues; a late `logOut()` that lands afterwards is harmless,
 * because RevenueCat identity is re-established from the organization by the billing screens.
 */
export const SUBSCRIPTION_CLEAR_CAP_MS = 3000

/**
 * Run `work()` but stop waiting for it after `capMs`.
 *
 * A REJECTION still rejects — a subscription clear that fails is a real fault and must reach
 * `onError` rather than be swallowed. Only the "never settles" case is converted into a resolve,
 * because that is the one the caller cannot otherwise survive.
 *
 * Deliberately a single `setTimeout` raced against the work, first-one-wins, for the same reason
 * `awaitReady` is: a poll or a self-rescheduling microtask spins forever under a frozen clock.
 *
 * @template T
 * @param {() => Promise<T> | T} work
 * @param {number} capMs
 * @param {string} label
 * @returns {Promise<T | undefined>}
 */
function callWithCap(work, capMs, label) {
  return new Promise((resolve, reject) => {
    let settled = false
    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      console.warn(`[analytics] ${label} did not settle within ${capMs}ms; continuing without it`)
      resolve(undefined)
    }, capMs)
    const finish = (settle) => (value) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      settle(value)
    }
    try {
      Promise.resolve(work()).then(finish(resolve), finish(reject))
    } catch (error) {
      // A synchronously throwing effect (a missing native module) is a fault, not a hang.
      finish(reject)(error)
    }
  })
}

/**
 * Wait for the SDK's persisted state, for at most `capMs`.
 *
 * Deliberately a single `setTimeout` raced against the SDK's own promise, first-one-wins, with the
 * timer cleared on the winning path. It must not become a poll or a self-rescheduling microtask:
 * under a frozen clock (fake timers, a paused JS thread) those spin forever rather than resolving,
 * which is the failure this cap exists to prevent, reintroduced one layer down.
 *
 * Never rejects. A client with no `ready()` at all resolves immediately.
 *
 * @param {{ready?: () => Promise<unknown>} | null | undefined} postHog
 * @param {number} capMs
 * @returns {Promise<void>}
 */
function awaitReady(postHog, capMs) {
  const ready = postHog?.ready
  if (typeof ready !== 'function') return Promise.resolve()
  return new Promise((resolve) => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve()
    }
    const timer = setTimeout(() => {
      console.warn(`[analytics] PostHog was not ready within ${capMs}ms; proceeding`)
      finish()
    }, capMs)
    try {
      Promise.resolve(ready.call(postHog)).then(finish, finish)
    } catch (error) {
      // A `ready()` that throws synchronously (a missing native module) is "as ready as it gets",
      // not a reason to stall — but it is worth naming, because it means the SDK is not working.
      console.warn('[analytics] PostHog ready() threw; proceeding', error)
      finish()
    }
  })
}

/**
 * Read the uid PostHog is currently identified as, or null when it is anonymous.
 *
 * This asks the SDK rather than keeping our own copy, on purpose. PostHog persists its identity to
 * disk, so its answer survives an app restart, a JS reload and an OTA update — whereas an in-memory
 * ref would report "anonymous" on every launch and re-alias on every session. The test
 * `distinctId !== anonymousId` is the same one PostHog's own `_isIdentified()` uses as its fallback,
 * so this cannot disagree with the `$is_identified` property the backend eventually sees.
 *
 * @param {{getDistinctId?: () => string, getAnonymousId?: () => string} | null | undefined} postHog
 * @returns {string | null}
 */
export function readIdentifiedUid(postHog) {
  try {
    const distinctId = postHog?.getDistinctId?.()
    const anonymousId = postHog?.getAnonymousId?.()
    if (!distinctId || !anonymousId) return null
    return distinctId === anonymousId ? null : distinctId
  } catch (error) {
    // A storage failure inside the SDK must not decide identity for us. Treating it as "unknown,
    // therefore anonymous" is the safe direction: the worst case is one redundant identify.
    console.warn('[analytics] could not read PostHog identity', error)
    return null
  }
}

/**
 * Decide what to do when the authenticated identity changes.
 *
 * @param {{identifiedAs: string | null | undefined, nextUid: string | null | undefined}} state
 * @returns {{reset: boolean, identify: string | null}}
 */
export function resolveIdentityTransition({ identifiedAs, nextUid }) {
  const current = identifiedAs || null
  const next = nextUid || null

  // Signed out. Clear the identity so the next person on this device starts anonymous instead of
  // inheriting the departed user's person. Nothing to clear when we were never identified — a
  // gratuitous reset would throw away an anonymous id that legitimately spans the pre-auth funnel.
  if (!next) return { reset: current != null, identify: null }

  // Already identified as exactly this person. PostHog has persisted `PersonMode: 'identified'`,
  // so `$is_identified` is already true and re-identifying would only add noise.
  if (current === next) return { reset: false, identify: null }

  // Anonymous → this person. NO reset: the anonymous distinct_id must survive into `identify()` so
  // PostHog merges the pre-auth funnel events (funnel_* screens, the whole signup journey) into the
  // new person. Resetting here is precisely how those events would be orphaned.
  if (current == null) return { reset: false, identify: next }

  // Identified as somebody ELSE. This is an account switch that never showed us a signed-out state
  // (a killed app mid-logout, a failed reset, a direct re-auth). Identifying straight from A to B
  // would make PostHog alias two real accounts into a single person — permanently, and across every
  // dashboard. Reset first so B starts from a fresh anonymous id that carries nothing of A's.
  return { reset: true, identify: next }
}

/**
 * Apply an identity transition to a PostHog client.
 *
 * Awaits `postHog.ready()` first — but BOUNDED (see `IDENTITY_READY_CAP_MS`). Without the await,
 * `getDistinctId()` can be read before the SDK has finished loading its persisted state, which would
 * report a freshly minted anonymous id for a device that is in fact still identified as a previous
 * user — and that misreading is the one that produces a cross-account alias. Without the BOUND, a
 * wedged SDK would hold up the signup navigation that awaits this. Ordering after the await is safe:
 * PostHog funnels `identify` and `capture` through the same init queue, so a call made now cannot
 * overtake one made earlier.
 *
 * @param {object | null | undefined} postHog
 * @param {string | null | undefined} nextUid the Firebase UID, or null when signed out
 * @param {{properties?: Record<string, unknown>, superProperties?: Record<string, unknown>, readyCapMs?: number}} [options]
 *   non-PII person properties to set, the super properties to restore after a reset (see below),
 *   and an override for the readiness cap (tests only)
 * @returns {Promise<{reset: boolean, identify: string | null}>} what was actually applied
 */
export async function syncAnalyticsIdentity(postHog, nextUid, options = {}) {
  const noop = { reset: false, identify: null }
  if (!postHog) return noop
  try {
    await awaitReady(postHog, options.readyCapMs ?? IDENTITY_READY_CAP_MS)
    const transition = resolveIdentityTransition({
      identifiedAs: readIdentifiedUid(postHog),
      nextUid,
    })
    if (transition.reset) {
      postHog.reset?.()
      // `reset()` is not selective: posthog-core nulls EVERY persisted key, `Props` included — so
      // it also throws away the ATL-531 build provenance (update_id, js_version, platform, …).
      // That payload is registered once per process at PostHogProvider mount by an effect keyed on
      // the PostHog client, and the client survives a logout, so nothing would ever re-run it:
      // every event for the rest of the process would go out without the fields the read-cutover
      // `ota-coverage` gate reads. Restore them on the same edge that clears them.
      if (options.superProperties) {
        void Promise.resolve(postHog.register?.(options.superProperties)).catch(() => {})
      }
    }
    if (transition.identify) postHog.identify?.(transition.identify, options.properties)
    return transition
  } catch (error) {
    console.warn('[analytics] identity sync failed; app continues', error)
    return noop
  }
}

/**
 * Capture the canonical successful-signup event, with identity established FIRST.
 *
 * The ordering is not a convention a caller can get wrong: identity is applied inside this function,
 * in the same call, before `capture` runs. `identify()` writes `PersonMode: 'identified'` to
 * PostHog's persisted properties synchronously within the SDK's init queue, and `capture()` reads
 * that same flag to stamp `$is_identified` — so a capture issued after an identify in this order is
 * guaranteed to be identified.
 *
 * **Without a uid there is no signup event.** Emitting an anonymous `user_signed_up` is the exact
 * defect being fixed, and a silently-anonymous event is worse than a missing one: it looks like
 * data. The caller is told (`false`) so it can report the anomaly rather than assume success. In
 * practice this cannot happen — `createNewUserAndOrganization` has already awaited
 * `createUserWithEmailAndPassword`, so a uid exists by the time the caller gets here.
 *
 * `organizationId` rides along as `$groups.organization` so the signup event is attributable to the
 * tenant it created, which is the level subscription lifecycle is joined at. It is an opaque
 * Firestore id, not personal data.
 *
 * @param {object | null | undefined} postHog
 * @param {{uid?: string | null, organizationId?: string | null, properties?: Record<string, unknown>, superProperties?: Record<string, unknown>, readyCapMs?: number}} params
 * @returns {Promise<boolean>} true when the event was captured under an established identity
 */
export async function captureSignupCompleted(
  postHog,
  { uid, organizationId, properties, superProperties, readyCapMs } = {},
) {
  if (!postHog || !uid) return false
  try {
    await syncAnalyticsIdentity(postHog, uid, { superProperties, readyCapMs })

    // Defence in depth: if identity did not take (a disabled client, a storage failure swallowed
    // inside the SDK), refuse rather than emit the anonymous event this ticket exists to remove.
    if (readIdentifiedUid(postHog) !== uid) return false

    postHog.capture?.(SIGNUP_COMPLETED_EVENT, {
      ...buildSignupProperties(organizationId),
      ...(properties || {}),
    })
    return true
  } catch (error) {
    console.warn('[analytics] signup capture failed; signup itself is unaffected', error)
    return false
  }
}

/**
 * Build the handler that binds Firebase Auth's session to analytics identity.
 *
 * This is the whole behaviour of `global/AnalyticsIdentityBridge.js`, lifted out of React so it can
 * be asserted directly. That matters more than usual here: the bridge is the ONLY thing that
 * establishes identity for a returning user and the only thing that ends it on logout, and neither
 * path is reachable from a unit test that has to mount a component wired to Firebase, RevenueCat,
 * Sentry and PostHog. Injecting the two effects it needs makes launch, logout and account switch
 * testable as sequences of auth callbacks — which is exactly how they occur in production.
 *
 * `lastHandledUid` starts as `undefined`, distinct from `null`. Firebase re-emits on token refresh,
 * so repeated callbacks for the same user are ignored; and the very first callback of a process is
 * not a session ENDING, so launching signed-out must not fire a subscription logout for a session
 * that never existed. The authoritative previous identity still comes from PostHog's own persisted
 * state (see `readIdentifiedUid`) — this ref only suppresses redundant work.
 *
 * @param {{
 *   postHog: object | null | undefined,
 *   superProperties?: Record<string, unknown>,
 *   clearSubscriptionIdentity?: () => Promise<boolean> | boolean,
 *   clearSubscriptionCapMs?: number,
 *   onError?: (error: unknown) => void,
 *   onIdentitySwitchedWithoutSignOut?: () => void,
 * }} deps
 * @returns {(authUser: {uid?: string} | null | undefined) => Promise<{
 *   skipped: boolean,
 *   transition?: {reset: boolean, identify: string | null},
 *   clearedSubscription?: boolean,
 * }>}
 */
export function createAuthIdentityHandler({
  postHog,
  superProperties,
  clearSubscriptionIdentity,
  clearSubscriptionCapMs,
  onError,
  onIdentitySwitchedWithoutSignOut,
}) {
  let lastHandledUid
  // Auth callbacks are not spaced out: a logout followed immediately by a sign-in delivers two
  // while the first is still awaiting the SDK's readiness. Each one MUTATES identity, so they are
  // run strictly one after another — otherwise a slow (or capped-out) readiness wait could let a
  // sign-out's `reset()` land after the next person's `identify()` and leave them anonymous, which
  // is the very defect this bridge exists to remove.
  let queue = Promise.resolve()

  return function handleAuthUser(authUser) {
    const result = queue.then(() => applyAuthUser(authUser))
    queue = result.then(
      () => undefined,
      () => undefined,
    )
    return result
  }

  async function applyAuthUser(authUser) {
    const uid = authUser?.uid || null
    if (lastHandledUid === uid) return { skipped: true }
    const previousUid = lastHandledUid
    lastHandledUid = uid

    try {
      const transition = await syncAnalyticsIdentity(postHog, uid, { superProperties })

      // A sign-out. Clear the subscription identity on the SAME edge, so the two systems can never
      // disagree about whether this device still belongs to somebody.
      let clearedSubscription = false
      if (!uid && previousUid && clearSubscriptionIdentity) {
        // Bounded: this is the only unbounded await in the serialized queue (see
        // SUBSCRIPTION_CLEAR_CAP_MS). A hung native call must not stop the next sign-in from
        // being identified.
        clearedSubscription =
          (await callWithCap(
            () => clearSubscriptionIdentity(),
            clearSubscriptionCapMs ?? SUBSCRIPTION_CLEAR_CAP_MS,
            'subscription identity clear',
          )) === true
      }

      // An account switch we only learned about at the switch itself: a sign-out edge was missed.
      // Worth reporting — it is the shape that used to alias two real accounts into one person.
      if (transition.reset && transition.identify) onIdentitySwitchedWithoutSignOut?.()

      return { skipped: false, transition, clearedSubscription }
    } catch (error) {
      // Identity is telemetry. It must never take the app down (ATL-901).
      onError?.(error)
      return { skipped: false }
    }
  }
}

/**
 * Non-PII properties for the signup event. No email, no name, no phone, no device identifier —
 * only opaque tenant/platform ids that already exist elsewhere in the analytics schema.
 *
 * @param {string | null | undefined} organizationId
 * @returns {Record<string, unknown>}
 */
export function buildSignupProperties(organizationId) {
  return organizationId ? { $groups: { organization: organizationId } } : {}
}
